import React, { useState, useCallback, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { expertService, bookingService } from '../api/services';
import { Download, FileText, Calendar, Clock, Video, Mic, MessageCircle, Loader2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const TYPE_ICON = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };

const TimesheetPage = ({ role = 'EXPERT' }) => {
  const isExpert = role === 'EXPERT';
  const currentYear  = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [year,        setYear]        = useState(currentYear);
  const [month,       setMonth]       = useState(currentMonth);
  const [timesheet,   setTimesheet]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchTimesheet = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { year, month };
      const res = isExpert
        ? await expertService.getMyTimesheet(params)
        : await bookingService.getMyTimesheet(params);
      const body = res.data?.data ?? res.data;
      setTimesheet(body?.timesheet ?? body);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load timesheet');
    } finally { setLoading(false); }
  }, [year, month, isExpert]);

  useEffect(() => { fetchTimesheet(); }, [fetchTimesheet]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = { year, month };
      const res = isExpert
        ? await expertService.downloadMyTimesheet(params)
        : await bookingService.downloadMyTimesheet(params);
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `Timesheet-${MONTHS[month]}-${year}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Timesheet downloaded');
    } catch {
      toast.error('Could not generate timesheet');
    } finally { setDownloading(false); }
  };

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const sessions  = timesheet?.sessions  ?? [];
  const summary   = timesheet?.summary   ?? {};

  return (
    <DashboardLayout title="My Timesheet">
      <div className="max-w-4xl space-y-5">

        <p className="text-sm text-forest-500">
          {isExpert
            ? 'A record of all sessions you have completed with clients.'
            : 'A record of all sessions you have attended with experts.'}
        </p>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <select value={month} onChange={e => setMonth(Number(e.target.value))}
            className="px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white text-forest-700">
            {MONTHS.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            className="px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white text-forest-700">
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={handleDownload} disabled={downloading || sessions.length === 0}
            className="flex items-center gap-2 text-sm font-medium bg-forest-800 text-cream-50 px-5 py-2.5 rounded-full hover:bg-forest-700 transition-colors disabled:opacity-50">
            {downloading
              ? <><Loader2 size={14} className="animate-spin" /> Generating...</>
              : <><Download size={14} /> Download PDF</>}
          </button>
        </div>

        {/* Summary cards */}
        {timesheet && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
              <p className="font-serif text-3xl font-bold text-forest-900">{summary.totalSessions ?? 0}</p>
              <p className="text-xs text-forest-400 uppercase tracking-wider mt-1">Sessions</p>
            </div>
            <div className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
              <p className="font-serif text-3xl font-bold text-forest-900">{summary.totalHours ?? '0.00'}</p>
              <p className="text-xs text-forest-400 uppercase tracking-wider mt-1">Total Hours</p>
            </div>
            <div className="bg-white border border-cream-200 rounded-2xl p-4 text-center">
              <p className="font-serif text-3xl font-bold text-forest-900">{MONTHS[month]}</p>
              <p className="text-xs text-forest-400 uppercase tracking-wider mt-1">{year}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        {/* Session table */}
        <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-cream-50 border-b border-cream-100 text-[10px] font-semibold text-forest-500 uppercase tracking-widest">
            <div className="col-span-2">Type</div>
            <div className="col-span-3">Date</div>
            <div className="col-span-2">Time</div>
            <div className="col-span-2">Duration</div>
            <div className="col-span-3">{isExpert ? 'Client' : 'Expert'}</div>
          </div>

          {loading ? (
            [...Array(5)].map((_, i) => (
              <div key={i} className="h-14 px-5 flex items-center gap-3 animate-pulse border-b border-cream-50">
                <div className="w-8 h-8 rounded-full bg-cream-200" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-cream-200 rounded w-1/3" />
                  <div className="h-2.5 bg-cream-100 rounded w-1/4" />
                </div>
              </div>
            ))
          ) : sessions.length === 0 ? (
            <div className="text-center py-16">
              <Calendar size={32} className="text-forest-200 mx-auto mb-3" />
              <p className="font-serif text-xl text-forest-600">No sessions in {MONTHS[month]} {year}</p>
              <p className="text-sm text-forest-400 mt-1">Try selecting a different month</p>
            </div>
          ) : (
            <div className="divide-y divide-cream-50">
              {sessions.map((s, i) => {
                const Icon = TYPE_ICON[s.sessionType] ?? Video;
                const counterpart = isExpert ? s.clientName : s.expertName;
                return (
                  <div key={i} className="grid grid-cols-12 gap-3 px-5 py-4 items-center hover:bg-cream-50/50 transition-colors">
                    <div className="col-span-2 flex items-center gap-1.5">
                      <div className="w-7 h-7 bg-forest-50 rounded-full flex items-center justify-center">
                        <Icon size={12} className="text-forest-600" />
                      </div>
                      <span className="text-[10px] text-forest-400 hidden sm:block">{s.sessionType}</span>
                    </div>
                    <div className="col-span-3">
                      <p className="text-sm text-forest-800">{s.date}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-forest-600">{s.startTime}</p>
                      <p className="text-[10px] text-forest-400">→ {s.endTime}</p>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-1 text-xs text-forest-600">
                        <Clock size={10} className="text-forest-400" />
                        {s.durationHours}h
                      </div>
                    </div>
                    <div className="col-span-3">
                      <p className="text-sm text-forest-700 truncate">{counterpart || '—'}</p>
                      {s.title && <p className="text-[10px] text-forest-400 truncate">{s.title}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {sessions.length > 0 && (
          <p className="text-xs text-forest-400 text-center">
            Showing {sessions.length} completed session{sessions.length !== 1 ? 's' : ''} for {MONTHS[month]} {year}
          </p>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TimesheetPage;